import React, { useState } from 'react';
import Hero from '../../../components/Hero/Hero';
import Pathfinder from '../../../components/Pathfinder/Pathfinder';
import Metadata from '../../../components/Metadata/Metadata';
import Section from '../../../components/Section/Section';
import Card from '../../../components/Card/Card';
import GlobalCallout from '../../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Accordion from '../../../components/Accordion/Accordion';
import Wallpaper from '../../../components/Wallpaper/Wallpaper';
import Button from '../../../components/Button/Button';
import Image from '../../../components/Image/Image';
import ContentSplit from '../../../components/ContentSplit/ContentSplit';
import ListItem from '../../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Custom Informatics Solutions | General Genomics"
        description="Discover General Genomics’ expertise in creating tailored data solutions for commercial clients. Learn how advanced analytics and machine learning solve complex challenges and meet specific business objectives."
      />
      <Hero
        helper="Industries &gt; Commercial"
        headline="AI-driven data solutions transforming the field of informatics"
      />
      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Empowering Data-Driven Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Unlock the power of personalized informatics with General Genomics.
          Our custom solutions blend advanced analytics, machine learning, and
          tailored tools to solve your unique challenges and drive impactful
          results. Serving industries such as pharmaceuticals, insurance, and
          research, we specialize in optimizing workflows, improving data
          accuracy, and predicting outcomes. Partner with us to transform your
          data into actionable insights, streamline operations, and achieve your
          specific business objectives. Contact us today and discover how our
          expertise can empower your organization to excel.
        </p>
        <Image
          path="/images/library/woman-dashboard.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/lab-technicians-group.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">Why Choose Us?</p>
        <h2 className="action-on-view action-fade-in">
          Tailored Solutions, Proven Impact
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Industry Expertise:</strong> Extensive experience in
            pharmaceuticals, insurance, and research ensures solutions are
            tailored to your needs.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Advanced Analytics:</strong> Leverage cutting-edge machine
            learning to optimize workflows and deliver actionable insights.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Proven Success:</strong> Case studies demonstrate our
            ability to solve complex challenges and achieve measurable results.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Scalable Solutions:</strong> Flexible, customizable
            approaches adapt to your organization’s unique goals and size.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">Explore Our Solutions</h2>
        <p className="action-on-view action-fade-in">
          Discover more about how General Genomics can support your business
          with innovative solutions in pharmaceuticals, insurance, and more.
        </p>
        <div className="card-group">
          <Card
            icon="local_pharmacy"
            headline="Pharmaceuticals"
            subhead="Unlock drug discovery insights and analyze patient data with our genomics solutions."
            btnURL="/industries/commercial/pharmaceuticals"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="account_balance"
            headline="Insurance & Payers"
            subhead="Optimize risk modeling and enhance claims processing with genomics-powered analytics."
            btnURL="/industries/commercial/insurance-and-payers"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="science"
            headline="Genomic Research"
            subhead="Dive deeper into our cutting-edge genomic research and its impact across industries."
            btnURL="/industries/commercial/researchers"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../../images/library/office-startup-2.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about Custom Informatics Solutions
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn how General Genomics tailors data solutions to meet diverse
          industry needs with advanced analytics and machine learning.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What industries does General Genomics serve with custom informatics solutions?"
          >
            General Genomics provides tailored data solutions for various{' '}
            <Link to="/industries">industries,</Link> including{' '}
            <Link to="/industries/commercial/pharmaceuticals">
              pharmaceuticals,
            </Link>{' '}
            <Link to="/industries/commercial/insurance-and-payers">
              insurance,
            </Link>{' '}
            and <Link to="/industries/commercial/researchers">research,</Link>{' '}
            addressing specific challenges like workflow optimization, data
            accuracy, and predictive modeling.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can custom informatics solutions improve my business?"
          >
            Our bespoke solutions leverage advanced analytics and machine
            learning to help businesses optimize workflows, predict outcomes,
            and enhance data accuracy, ultimately improving decision-making and
            operational efficiency.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What types of challenges can General Genomics help solve with custom data solutions?"
          >
            General Genomics specializes in addressing complex challenges such
            as optimizing business processes, improving data accuracy, and
            predicting key outcomes to drive better results across different{' '}
            <Link to="/industries">industries.</Link>
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can General Genomics provide case studies or examples of successful data solutions?"
          >
            Yes, we showcase several case studies that highlight how our custom
            informatics solutions have successfully helped organizations in
            various <Link to="/industries">industries</Link> solve specific
            business challenges and achieve their objectives.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
